import { useMemo } from 'react';

import { addDays, differenceInDays, max } from 'date-fns';

import { Ribbon, RibbonProps, Notification } from '@bitrise/bitkit';
import { dayCountLimitForHighlightedNagging, dayCountForSecondNagging } from './hooks/useShowBuildCacheTrial';

export type BuildCacheTrialNotificationProps = {
  as: 'Ribbon' | 'Notification';
  trialExpiresAt: string;
  onActivateSubscriptionSelected: () => void;
  onClose?: (isFinalRibbon: boolean, cookieExpiresAt: Date) => void;
};

const BuildCacheTrialNotification = ({
  as,
  trialExpiresAt,
  onActivateSubscriptionSelected,
  onClose,
}: BuildCacheTrialNotificationProps): JSX.Element => {
  const expiresAt = useMemo(() => {
    return new Date(trialExpiresAt);
  }, [trialExpiresAt]);
  const daysLeft = differenceInDays(expiresAt, new Date());
  const isFinalRibbon = daysLeft < 0;

  let colorScheme: RibbonProps['colorScheme'] = 'blue';
  if (daysLeft <= dayCountLimitForHighlightedNagging) {
    colorScheme = 'yellow';
  }
  if (isFinalRibbon) {
    colorScheme = 'blue';
  }

  const cookieExpiresAt = useMemo((): Date => {
    if (daysLeft >= dayCountForSecondNagging) {
      return max([addDays(expiresAt, -dayCountForSecondNagging), addDays(new Date(), 1)]);
    }
    if (daysLeft >= dayCountLimitForHighlightedNagging) {
      return max([addDays(expiresAt, -dayCountLimitForHighlightedNagging), addDays(new Date(), 1)]);
    }
    return max([expiresAt, addDays(new Date(), 1)]);
  }, [daysLeft, expiresAt]);

  const commonProps: any = {
    action: {
      label: 'Request subscription',
      onClick: onActivateSubscriptionSelected,
    },
  };
  if (onClose) {
    commonProps.onClose = () => {
      return onClose(isFinalRibbon, cookieExpiresAt);
    };
  }
  const text = isFinalRibbon ? <>Build Cache trial has ended.</> : <>{daysLeft} days left from Build Cache trial.</>;

  if (as === 'Ribbon') {
    return (
      <Ribbon {...commonProps} colorScheme={colorScheme} display="flex" gap={8}>
        {text}
      </Ribbon>
    );
  }

  return (
    <Notification {...commonProps} status="info">
      {text}
    </Notification>
  );
};

export default BuildCacheTrialNotification;
